import {useConfig} from '@aboutyou/config';
import {GetPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_cluster_page/v1/service.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  FullWidthImage,
  Image,
  SPACING,
  Text,
  UnstyledLinkLinaria,
  desktopAndUpLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, useEffect} from 'react';
import {GoBackButton} from '../../features/GoBackButton/GoBackButton';
import {
  StoriesWrapper,
  StyledHeightCalcWrapper,
  heroImageHeight,
} from './Shared';

export interface StoryClusterPageProps {
  preloadOutfitDetailPage: VoidFunction;
}

const storyImageBreakpoints = [{columns: 1}];
const storyImageWidths = [360, 440, 640, 860, 1080];

export const StoryClusterPageView: FC<
  GetPageResponse & StoryClusterPageProps
> = ({header, stories, preloadOutfitDetailPage}) => {
  const isMobile = useConfig().device === 'mobile';

  useEffect(() => {
    // legacy pages take longer to load, therefore we preload it to make the load faster
    preloadOutfitDetailPage();
  }, []);

  return (
    <section>
      <StyledHeightCalcWrapper>
        <BackButtonWrapper>
          <StyledBackButton />
        </BackButtonWrapper>
        <CoverSquareImage
          imageUrl={header.coverSquareImage.url}
          description={header.coverSquareImage.description}
          aspectRatio="1:25"
          highPrioImage
          disableFixedRatio
          imagePosition="top"
        />
        {!isMobile && (
          <CoverLandscapeImage
            imageUrl={header.coverLandscapeImage.url}
            description={header.coverLandscapeImage.description}
            aspectRatio="2:1"
            highPrioImage
            disableFixedRatio
            imagePosition="top"
          />
        )}
        <CoverImageTextWrapper>
          <Headline data-testid="headline">{header.headline}</Headline>
          {!isMobile && (
            <Subline data-testid="subline">{header.subline}</Subline>
          )}
        </CoverImageTextWrapper>
      </StyledHeightCalcWrapper>
      {isMobile && <Subline data-testid="subline">{header.subline}</Subline>}
      <StoriesWrapper data-testid="storiesWrapper">
        {stories.map((story, index) => {
          const isCloseToTop = index < 2;
          const aspectRatio: `${number}:${number}` = story.coverImage
            .aspectRatio
            ? `${story.coverImage.aspectRatio.width}:${story.coverImage.aspectRatio.height}`
            : '2.2:1';

          return (
            <TrackPromotions
              key={`${index}${story.link}`}
              promotion={{
                source: TrackingPromotionCreative.APIPromotion,
                apiPromotion: {
                  ...story.promotionTracker,
                  destinationUrl: story.link.url,
                },
              }}
              streamPosition={0}
              recoList={{
                sectionIndex: 0,
              }}
              triggerOnce
            >
              <Link to={story.link.url} aria-label={undefined}>
                <StoryImage
                  breakpoints={storyImageBreakpoints}
                  widths={storyImageWidths}
                  importance={isCloseToTop ? 'high' : 'auto'}
                  loading={isCloseToTop ? 'eager' : 'lazy'}
                  ratio={aspectRatio}
                  src={story.coverImage.url}
                  alt={story.coverImage.description}
                />
                <StorySubline>{story.subline}</StorySubline>
                <StoryHeadline>{story.headline}</StoryHeadline>
              </Link>
            </TrackPromotions>
          );
        })}
      </StoriesWrapper>
    </section>
  );
};

const BackButtonWrapper = styled.div`
  width: 100%;
  max-width: 1800px;
  margin-inline: auto;
  align-self: flex-start;
  justify-self: flex-start;

  && {
    z-index: 2;
  }
`;

const StyledBackButton = styled(GoBackButton)`
  &&& {
    fill: ${COLORS.White};
    padding: 13px !important;

    ${tabletAndUpLinaria(`
      border-radius: 50%;
      background: ${COLORS['Fill.White']};
      padding: ${SPACING['X.Md']} !important;
      margin: 24px 20px;

      
      svg { 
        fill: ${COLORS['Fill.Black']};
      }
    `)}

    ${desktopAndUpLinaria(`
      margin: 24px 60px;
    `)}
  }
`;

const CoverImageSharedStyles = `
  &::after {
    content: '';
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.4) 73.08%
    );
    position: absolute;
    inset: 0;
    z-index: 1;
  }
`;

// the landscape cover image doesn't look good on iPads pro,
// therefore we decided to add a custom breakpoint to only display
// the landscape one from 1025px width
const mediaQueryDesktop1025pxAndUp = (styles: string) =>
  `@media only screen and (min-width: 1025px) {${styles}}`;

const CoverSquareImage = styled(FullWidthImage)`
  position: relative;
  height: 100%;
  aspect-ratio: 1.25 / 1;

  ${CoverImageSharedStyles}

  ${mediaQueryDesktop1025pxAndUp(`
    display: none;
  `)}
`;

const CoverLandscapeImage = styled(FullWidthImage)`
  ${CoverImageSharedStyles}
  display: none;

  ${tabletAndUpLinaria(`
    &::after {
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 11.98%,
        rgba(0, 0, 0, 0.4) 61.41%
      );
    }
    
    ${heroImageHeight}
  `)}

  ${mediaQueryDesktop1025pxAndUp(`
    display: block;
  `)}
`;

const CoverImageTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  margin: ${SPACING['X.Xl']} ${SPACING['X.Lg']};

  ${tabletAndUpLinaria(`
    margin: ${SPACING['X.Xl4']} ${SPACING['X.Lg']};
  `)}
`;

const Headline = styled.h1`
  font-size: ${FONT_SIZES.px32};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.25;
  color: ${COLORS['Text.White']};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px48};
    line-height: 1.12;
  `)}
`;

const Subline = styled.h2`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.2;
  color: ${COLORS['Text.Black']};
  text-align: center;
  display: block;
  padding: ${SPACING['X.Xl']} ${SPACING['X.Lg']};
  margin: 0;

  ${tabletAndUpLinaria(`
    color: ${COLORS['Text.White']};
    font-size: ${FONT_SIZES.px20};
    padding: 0;
  `)}
`;

const Link = styled(UnstyledLinkLinaria)`
  display: flex;
  flex-direction: column;
`;

const StoryImage = styled(Image)`
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const StorySubline = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  letter-spacing: 0.48px;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const StoryHeadline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  line-height: 1.25;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};
  `)}
`;
